import React, { useState, useEffect, useRef } from "react";

const SearchDropdown = ({
  initialOptions = [],
  onSelect,
  onInputChange,
  placeholder = "Search...",
  label = "Search Dropdown",
  labelKey = (option) => option.label,
  dropdownStyle = {},
  inputStyle = {},
  optionStyle = {},
  optionClassName = "",
  selected = null,
  required = false,
  showError = false,
  disabled = false,
  initialValue = "",
}) => {
  const [searchKey, setSearchKey] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(initialOptions);
  const [showOptions, setShowOptions] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [displayValue, setDisplayValue] = useState(initialValue);
  const dropdownRef = useRef(null);
  const optionRefs = useRef([]);

  const hasError = showError && required && !selectedOption && !displayValue;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setFilteredOptions(initialOptions);
  }, [initialOptions]);

  useEffect(() => {
    if (selected && selectedOption?.city_id !== selected.city_id) {
      setSelectedOption(selected);
      setSearchKey("");
      setDisplayValue("");
    } else if (!selected) {
      setSelectedOption(null);
      setSearchKey("");
    }
  }, [selected]);

  // Sync displayValue when initialValue prop changes
  useEffect(() => {
    if (initialValue) {
      setDisplayValue(initialValue);
    } else {
      setDisplayValue("");
    }
  }, [initialValue]);

  useEffect(() => {
    if (searchKey === "") {
      setFilteredOptions(initialOptions);
    } else {
      setFilteredOptions(
        initialOptions.filter((option) =>
          labelKey(option).toLowerCase().includes(searchKey.toLowerCase())
        )
      );
    }
    setFocusedIndex(-1);
  }, [searchKey, initialOptions, labelKey]);

  useEffect(() => {
    if (focusedIndex > -1 && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [focusedIndex]);

  const handleKeyDown = (e) => {
    if (disabled) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setShowOptions(true);
      setFocusedIndex((prevIndex) =>
        prevIndex < filteredOptions.length - 1 ? prevIndex + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setShowOptions(true);
      setFocusedIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : filteredOptions.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        if (focusedIndex >= 0) {
          handleSelect(filteredOptions[focusedIndex]);
        } else {
          handleSelect(filteredOptions[0]);
        }
      }
    }
  };

  const handleSelect = (option) => {
    if (disabled) return;
    setSelectedOption(option);
    setDisplayValue("");
    onSelect(option);
    setSearchKey("");
    setShowOptions(false);
    setFocusedIndex(-1);
  };

  const handleClear = () => {
    if (disabled) return;
    setSelectedOption(null);
    setSearchKey("");
    setDisplayValue("");
    onSelect(null);
    setFocusedIndex(-1);
  };

  const getDisplayText = () => {
    if (searchKey) return searchKey;
    if (selectedOption) return labelKey(selectedOption);
    if (displayValue) return displayValue;
    return "";
  };

  return (
    <div
      className="search-dropdown"
      style={{
        position: "relative",
        ...dropdownStyle,
      }}
      ref={dropdownRef}
    >
      <label style={{ fontSize: "14px", color: "grey" }}>
        {label}
        {required && (
          <span style={{ color: "#ff0000", marginLeft: "5px" }}>*</span>
        )}
      </label>
      <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
        <input
          type="text"
          placeholder={placeholder}
          value={getDisplayText()}
          onChange={(e) => {
            if (disabled) return;
            setSearchKey(e.target.value);
            setDisplayValue("");
            if (onInputChange) onInputChange(e.target.value);
            setShowOptions(true);
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          style={{
            width: "100%",
            padding: "8px",
            color: "#6A6A6A",
            opacity: disabled ? 0.6 : 1,
            border: hasError ? "1px solid #ff0000" : "1px solid grey",
            cursor: disabled ? "not-allowed" : "text",
            ...inputStyle,
          }}
        />
        {(selectedOption || searchKey || displayValue) && !disabled && (
          <button
            onClick={handleClear}
            style={{
              position: "absolute",
              right: "8px",
              background: "none",
              border: "none",
              color: "grey",
              fontSize: "16px",
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        )}
      </div>
      {showOptions && !disabled && (
        <div
          className="dropdown-options"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            maxHeight: "200px",
            overflowY: "auto",
            border: "1px solid #ccc",
            backgroundColor: "#fff",
            zIndex: 1000,
            ...dropdownStyle,
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={index}
                ref={(el) => (optionRefs.current[index] = el)}
                onMouseEnter={() => setFocusedIndex(index)}
                onMouseLeave={() => setFocusedIndex(-1)}
                onClick={() => handleSelect(option)}
                style={{
                  padding: "8px",
                  cursor: "pointer",
                  color: "#6A6A6A",
                  backgroundColor:
                    index === focusedIndex ? "#f0f0f0" : "transparent",
                  ...optionStyle,
                }}
                className={optionClassName}
              >
                {labelKey(option)}
              </div>
            ))
          ) : (
            <div
              style={{
                padding: "8px",
                color: "#999",
                ...optionStyle,
              }}
            >
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;